import {useMutation, useQuery, UseQueryResult} from '@tanstack/react-query';
import {PublicationContext} from 'centrifuge';
import {camelizeKeys} from 'humps';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ReviewsStateType} from '../../../page/main/reviews/reviews-state/reviews-state-type';
import {useCentrifugeSubscription} from '../../../provider/centrifuge/centrifuge-hook';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useUser} from '../../../provider/user/user-hook';
import {
    AiChatMessageRoleEnum,
    ConcordanceAiPresetEnum,
    isReviewAnalysisSuggestAction,
    ReviewAnalysisAiMessageType,
} from '../../reivew-analysis/review-analysis-ai-chat/review-analysis-ai-message-type';

import {
    fetchReviewsAiChatEligibility,
    getReviewsAiChatEligibilityUrl,
    ReviewsAiChatEligibilityType,
    sendMessageToReviewsAiChat,
} from './reviews-ai-chat-api';

function getReviewsAiChatCentrifugeChannel(channelId: string) {
    return `public:rocket_gpt_${channelId}`;
}

export function useReviewsAiChat(reviewsState: ReviewsStateType): {
    messages: Array<ReviewAnalysisAiMessageType>;
    answerText: string;
    sendMessage: (options: {message: string; presetKey?: ConcordanceAiPresetEnum}) => void;
    isTyping: boolean;
    dialogId: string;
} {
    const {getLocalizedString} = useLocale();
    const [isTyping, setIsTyping] = useState(false);
    const [answerText, setAnswerText] = useState<string>('');

    const [messages, setMessages] = useState<Array<ReviewAnalysisAiMessageType>>([
        {
            role: AiChatMessageRoleEnum.SYSTEM,
            title: getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__GREETINGS_TITLE'),
            text: getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__GREETINGS_TEXT'),
        },
    ]);

    const channelId = useMemo(() => uuidv4(), []);
    const {user} = useUser();

    const {getSubscription} = useCentrifugeSubscription();

    const onResponseReceived = useCallback(
        (responseText: string) => {
            setIsTyping(false);
            setMessages((previous) => [
                ...previous,
                {
                    role: AiChatMessageRoleEnum.ASSISTANT,
                    text: responseText,
                },
            ]);
        },
        [setIsTyping, setMessages]
    );

    useEffect(() => {
        function onActionReceived({data}: PublicationContext) {
            const camelcaseData = camelizeKeys(data);

            if (isReviewAnalysisSuggestAction(camelcaseData)) {
                if (camelcaseData.stop) {
                    setIsTyping(false);
                    setAnswerText((previousState) => {
                        onResponseReceived(previousState);
                        return '';
                    });
                    return;
                }

                setAnswerText((previousState) => previousState + camelcaseData.text);
            }
        }

        const subscription = getSubscription(getReviewsAiChatCentrifugeChannel(channelId));

        subscription?.on('publication', onActionReceived);

        return () => {
            subscription?.removeListener('publication', onActionReceived);
        };
    }, [channelId, setAnswerText, getSubscription, user, onResponseReceived, answerText]);

    const {mutate: sendMessage} = useMutation({
        mutationFn: async (options: {message: string; presetKey?: ConcordanceAiPresetEnum}) => {
            setIsTyping(true);
            setMessages((previous) => [
                ...previous,
                {
                    preset: options.presetKey,
                    role: AiChatMessageRoleEnum.USER,
                    text: options.message,
                },
            ]);

            await sendMessageToReviewsAiChat({
                reviewsState,
                channelId,
                requestId: uuidv4(),
                presetKey: options.presetKey,
                message: options.message,
            }).catch((error) => {
                setIsTyping(false);

                if ('statusCode' in error && error.statusCode === 412) {
                    setMessages((previous) => [
                        ...previous,
                        {
                            text: getLocalizedString('REVIEWS_ANALYSIS__AI_ANALYSIS__ERROR_LIMITS_REACHED'),
                            role: AiChatMessageRoleEnum.ASSISTANT,
                        },
                    ]);
                } else {
                    setMessages((previous) => [
                        ...previous,
                        {
                            text: getLocalizedString('ERROR__SOMETHING_WENT_WRONG'),
                            role: AiChatMessageRoleEnum.ASSISTANT,
                        },
                    ]);
                }
            });
        },
    });

    return {
        messages,
        answerText,
        sendMessage,
        dialogId: channelId,
        isTyping,
    };
}

export function useReviewsAiChatEligibility(
    reviewsState: ReviewsStateType,
    enabled: boolean
): UseQueryResult<ReviewsAiChatEligibilityType> {
    return useQuery([getReviewsAiChatEligibilityUrl(reviewsState)], () => fetchReviewsAiChatEligibility(reviewsState), {
        enabled,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    });
}
